/**
 * PATH: packages/pricelist/src/columns.ts
 * FILE_ID: PKG-PRICELIST-COLUMNS-001
 * PURPOSE: Pure I→O helpers that turn a section's column_schema into AG-Grid
 *          column defs + flatten/unflatten PricelistItem rows for the grid.
 * WHY: The grid is generic — every section declares its own columns. Computed
 *      columns reuse the shared @quotech/quote-builder formula evaluator so a
 *      "=base_cost*1.1" cell means the same here as in the quote builder.
 * RELATED: types.ts, @quotech/quote-builder/formula/evaluate, PricelistWorkbook
 */
import { evaluateFormula } from '@quotech/quote-builder'
import type { ColumnDef, PricelistItem } from './types'
import { IQ_GATED_COLUMN_KEYS, PRICELIST_VISIBLE_DEFAULT_KEYS } from './iqSchema'

/** Core (non-schema) columns every section row carries. */
export const CORE_FIELDS = [
  'item_code',
  'item_name',
  'unit',
  'base_cost',
  'markup_percent',
  'computed_sell_price',
] as const

const CORE_LABELS: Record<string, string> = {
  item_code: 'Code',
  item_name: 'Description',
  unit: 'Unit',
  base_cost: 'Cost',
  markup_percent: 'Markup %',
  computed_sell_price: 'Sell',
}

export interface GridRow {
  id: string
  __new?: boolean
  __dirty?: boolean
  __deleted?: boolean
  is_active?: boolean
  position?: number
  [key: string]: unknown
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * Suppliers whose product Code (SKU) ALREADY encodes the supplier (the backend
 * builds these SKUs as `SUPPLIERCODE_...`), so prefixing again would be
 * redundant (e.g. `Euclid Chemical_EUCLID_...`). Matched by normalised prefix
 * so "Fatra Australia", "MapeProof" and "Victorian Membrane Supplies" all hit.
 * Suppliers NOT listed here (Bayset, Sika, …) get the `<Supplier>_` prefix.
 */
const SELF_ENCODED_SUPPLIER_TOKENS = [
  'euclid',
  'flowcrete',
  'fatra',
  'mapei',
  'mapeproof',
  'soprema',
  'tremco',
  'victorianmembrane',
  'wolfin',
] as const

/** lowercase + strip everything but a-z0-9 ("Euclid Chemical" -> "euclidchemical"). */
const normaliseSupplier = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

/** True when the supplier's code is already baked into the SKU (skip prefixing). */
export function supplierAlreadyEncoded(supplier: string): boolean {
  const norm = normaliseSupplier(supplier)
  if (!norm) return false
  return SELF_ENCODED_SUPPLIER_TOKENS.some((tok) => norm.startsWith(tok))
}

/**
 * True when the SKU already encodes its supplier as the leading
 * underscore-delimited segment — e.g. `MAPEI_1666220_10_L`, `BAYSET_WPA_419_150`.
 * The importer builds EVERY catalog SKU as `<SUPPLIER>_<code>` (UID_SKU_RULES),
 * so such codes must never be prefixed again (that produced the double/garbled
 * `Specialty Waterproofing Products / Mapei_MAPEI_…`). This is the robust,
 * supplier-name-agnostic guard: it reads the code itself, so a messy FK display
 * name like "Specialty Waterproofing Products / Mapei" can't defeat it.
 */
export function skuIsSelfEncoded(code: string): boolean {
  return /^[A-Za-z][A-Za-z0-9]*_/.test(code)
}

/**
 * Display the Code (SKU), prefixing it with its supplier ONLY when the code does
 * not already encode one, e.g. a bare `ABC123` → `Bayset_ABC123`.
 *
 * First principles: the Code (SKU) is intrinsic to the item, so any prefix is
 * derived from the item's own supplier — never the active section/filter — and
 * is identical in every view. A code that already leads with a supplier token
 * (`skuIsSelfEncoded`) or whose supplier name is itself baked into the code
 * (`supplierAlreadyEncoded`) is returned untouched — no second prefix. This
 * strips the redundant leading segment for Mapei/Bayset/etc., leaving the raw
 * supplier-encoded SKU. Display-only: `code` is never mutated.
 */
export function codeWithSupplierPrefix(code: unknown, supplier: unknown): string {
  const codeStr = code == null ? '' : String(code)
  const supplierStr = supplier == null ? '' : String(supplier).trim()
  if (!supplierStr || !codeStr) return codeStr
  if (skuIsSelfEncoded(codeStr) || supplierAlreadyEncoded(supplierStr)) return codeStr
  const prefix = `${supplierStr}_`
  return codeStr.startsWith(prefix) ? codeStr : `${prefix}${codeStr}`
}

/** PricelistItem → flat grid row (data.* hoisted to top-level keys). */
export function itemToRow(item: PricelistItem): GridRow {
  const row: GridRow = {
    id: item.id,
    item_code: item.item_code ?? '',
    item_name: item.item_name ?? '',
    unit: item.unit ?? '',
    base_cost: item.base_cost ?? null,
    markup_percent: item.markup_percent ?? null,
    computed_sell_price: item.computed_sell_price ?? null,
    is_active: item.is_active ?? true,
    position: item.position ?? 0,
  }
  for (const [k, v] of Object.entries(item.data ?? {})) {
    if (!(k in row)) row[k] = v as unknown
  }
  // The item's supplier is intrinsic to the row (used to prefix the Code/SKU).
  // Prefer the unified-schema data value; fall back to the FK-derived
  // supplier_name the API serialises at the top level so legacy items with a
  // supplier FK (but no data.supplier_name) still carry it.
  if (!row.supplier_name && item.supplier_name) row.supplier_name = item.supplier_name
  return row
}

/** Grid row → {core fields, data:{schema keys}} for a bulk-upsert payload. */
export function rowToUpsert(row: GridRow, schema: ColumnDef[]) {
  const data: Record<string, unknown> = {}
  for (const col of schema) {
    if (col.computed_from) continue // derived, never persisted
    if (col.key in row) data[col.key] = row[col.key]
  }
  return {
    id: row.__new ? null : row.id,
    item_code: typeof row.item_code === 'string' ? row.item_code : undefined,
    item_name: String(row.item_name ?? ''),
    unit: typeof row.unit === 'string' ? row.unit : undefined,
    base_cost: row.base_cost == null || row.base_cost === '' ? null : num(row.base_cost),
    markup_percent:
      row.markup_percent == null || row.markup_percent === '' ? null : num(row.markup_percent),
    data,
    position: row.position ?? null,
    is_active: row.is_active ?? true,
  }
}

/** Evaluate every computed column for one row against its own values. */
export function applyComputed(row: GridRow, schema: ColumnDef[]): GridRow {
  const ctx: Record<string, number> = {
    base_cost: num(row.base_cost),
    markup_percent: num(row.markup_percent),
  }
  for (const col of schema) {
    if (!col.computed_from) ctx[col.key] = num(row[col.key])
  }
  const next = { ...row }
  for (const col of schema) {
    if (col.computed_from) {
      const v = evaluateFormula(col.computed_from, ctx)
      next[col.key] = v
      ctx[col.key] = v ?? 0
    }
  }
  return next
}

const editorFor = (col: ColumnDef) => {
  if (col.data_type === 'enum')
    return { cellEditor: 'agSelectCellEditor', cellEditorParams: { values: col.enum_values ?? [] } }
  if (col.data_type === 'number' || col.data_type === 'money')
    return { cellEditor: 'agNumberCellEditor' }
  return { cellEditor: 'agTextCellEditor' }
}

/**
 * Effective alignment for a column: explicit `align` override wins; otherwise
 * number/money columns centre (Excel-style, 2026-07-14 meeting) and text-like
 * columns (product, codes, descriptions) stay left.
 */
export function effectiveAlign(col: Pick<ColumnDef, 'data_type' | 'align'>): 'left' | 'center' | 'right' {
  if (col.align === 'left' || col.align === 'center' || col.align === 'right') return col.align
  return col.data_type === 'number' || col.data_type === 'money' ? 'center' : 'left'
}

/** Grid cell/header class for an alignment (left = default, no class). */
export const alignClass = (align: 'left' | 'center' | 'right'): string | undefined =>
  align === 'center' ? 'qb-pl-align-center' : align === 'right' ? 'qb-pl-align-right' : undefined

/** Empty-ish cell value (null, undefined, '') — always sorts to the bottom. */
const isBlank = (v: unknown): boolean => v == null || v === ''

/** Numeric sort: parses stringy numbers so "9" < "10" (not lexicographic). */
const numericComparator = (a: unknown, b: unknown): number => {
  if (isBlank(a) && isBlank(b)) return 0
  if (isBlank(a)) return 1
  if (isBlank(b)) return -1
  const na = Number(a)
  const nb = Number(b)
  if (Number.isNaN(na) && Number.isNaN(nb)) return String(a).localeCompare(String(b))
  if (Number.isNaN(na)) return 1
  if (Number.isNaN(nb)) return -1
  return na - nb
}

/** Chronological sort for date columns (handles "Feb 2026" and ISO alike). */
const dateComparator = (a: unknown, b: unknown): number => {
  if (isBlank(a) && isBlank(b)) return 0
  if (isBlank(a)) return 1
  if (isBlank(b)) return -1
  const ta = Date.parse(String(a))
  const tb = Date.parse(String(b))
  if (Number.isNaN(ta) && Number.isNaN(tb)) return String(a).localeCompare(String(b))
  if (Number.isNaN(ta)) return 1
  if (Number.isNaN(tb)) return -1
  return ta - tb
}

/**
 * Type-aware sort comparator so every column sorts by MEANING, not raw text:
 * number/money numerically, date chronologically. Text/enum/bool/url fall back
 * to AG-Grid's default (locale string) compare — returns undefined for those.
 */
export function comparatorFor(
  col: Pick<ColumnDef, 'data_type'>,
): ((a: unknown, b: unknown) => number) | undefined {
  if (col.data_type === 'number' || col.data_type === 'money') return numericComparator
  if (col.data_type === 'date') return dateComparator
  return undefined
}

/** Build AG-Grid column defs: core columns + schema columns (+ computed).
 *
 * Unified pricelist sections carry deterministic `sku` + `uid` columns. On those
 * sections the structural `item_code` ("Code") column is a redundant duplicate —
 * it mirrors the UID on imported rows and the SKU on back-filled rows. So we lead
 * with SKU (relabelled "Code (SKU)") then UID, drop the `item_code` column, and
 * don't repeat sku/uid in the dynamic block — leaving ONE code column. Sections
 * without sku+uid (e.g. SAM steel, other tenants' tabs) keep the legacy
 * item_code-first layout untouched.
 */
export interface BuildColumnDefsOptions {
  editable: boolean
  /**
   * false = pricelist is supplier COST data only: the Markup % and Sell core
   * columns are omitted (markup/sell live in the quote builder, not here).
   * Defaults to true (legacy layout) so non-Bravada hosts are unaffected.
   */
  pricingColumns?: boolean
  /** true = render columns flagged hidden_by_default (admin "show hidden"). */
  showHidden?: boolean
  /** true = render IQ-gated fields. Omitted/false fail closed to the visible allowlist. */
  iqEnabled?: boolean
}

export function buildColumnDefs(schema: ColumnDef[], opts: BuildColumnDefsOptions) {
  const keys = new Set(schema.map((col) => col.key))
  const unified = keys.has('sku') && keys.has('uid')
  const showHidden = opts.showHidden === true
  const iqEnabled = opts.iqEnabled === true
  const isIqVisible = (key: string) =>
    iqEnabled ||
    (PRICELIST_VISIBLE_DEFAULT_KEYS.has(key) && !IQ_GATED_COLUMN_KEYS.has(key))
  const isHidden = (key: string) =>
    !isIqVisible(key) ||
    (!showHidden && schema.some((col) => col.key === key && col.hidden_by_default === true))

  // SKU first (as the single "Code (SKU)" column), then UID. Both are
  // deterministic identifiers and must never be hand-edited.
  const lead = unified
    ? [
        {
          field: 'sku',
          headerName: 'Code (SKU)',
          editable: false,
          minWidth: 160,
          sortable: true,
          cellEditor: 'agTextCellEditor',
          // Prefix with the item's OWN supplier (intrinsic → same in every view,
          // independent of the section tab / supplier filter).
          valueGetter: (params: { data?: GridRow }) =>
            codeWithSupplierPrefix(params?.data?.sku, params?.data?.supplier_name),
        },
        {
          field: 'uid',
          headerName: 'UID',
          editable: false,
          minWidth: 140,
          sortable: true,
          cellEditor: 'agTextCellEditor',
        },
      ].filter((def) => !isHidden(def.field))
    : []

  let coreFields: readonly string[] = unified
    ? CORE_FIELDS.filter((field) => field !== 'item_code')
    : CORE_FIELDS
  if (opts.pricingColumns === false) {
    coreFields = coreFields.filter(
      (field) => field !== 'markup_percent' && field !== 'computed_sell_price',
    )
  }
  coreFields = coreFields.filter(isIqVisible)
  // Core numeric columns centre like schema number/money columns; a schema
  // entry with the same key (align override saved by the manager) wins.
  const coreAlignOverride = (field: string) => schema.find((col) => col.key === field)?.align
  const core = coreFields.map((field) => {
    const numeric = ['base_cost', 'markup_percent', 'computed_sell_price'].includes(field)
    const cls = alignClass(
      effectiveAlign({ data_type: numeric ? 'number' : 'text', align: coreAlignOverride(field) }),
    )
    return {
      field,
      headerName: CORE_LABELS[field],
      editable: opts.editable && field !== 'computed_sell_price',
      minWidth: field === 'item_name' ? 220 : 110,
      flex: field === 'item_name' ? 2 : undefined,
      sortable: true,
      comparator: numeric ? numericComparator : undefined,
      cellClass: cls,
      headerClass: cls,
      cellEditor: ['base_cost', 'markup_percent'].includes(field)
        ? 'agNumberCellEditor'
        : 'agTextCellEditor',
    }
  })

  const dynamic = schema
    .filter((col) => !(unified && (col.key === 'sku' || col.key === 'uid')))
    .filter((col) => isIqVisible(col.key))
    .filter((col) => showHidden || col.hidden_by_default !== true)
    .map((col) => {
      // Schema attribute columns (dates, yes/no, notes, links, qtys) centre by
      // default — Excel-style; an explicit per-column align override still wins.
      const align = col.align === 'left' || col.align === 'center' || col.align === 'right'
        ? col.align
        : 'center'
      const cls = alignClass(align)
      const cellClasses = [col.computed_from ? 'qb-pl-computed' : undefined, cls].filter(
        (c): c is string => !!c,
      )
      return {
        field: col.key,
        headerName: col.label || col.key,
        editable: opts.editable && !col.computed_from,
        minWidth: 120,
        sortable: true,
        comparator: comparatorFor(col),
        headerTooltip: col.computed_from ? `= ${col.computed_from}` : col.unit || undefined,
        cellClass: cellClasses.length ? cellClasses : undefined,
        headerClass: cls,
        ...editorFor(col),
      }
    })

  return [...lead, ...core, ...dynamic]
}

/**
 * Column defs for the read-only "All sections" overview tab. The org schema is
 * UNIFORM across sections (columns/apply_view keeps every tab identical), so
 * the overview renders the SAME columns in the SAME order as every section tab
 * — plus a leading "Section" column so each row is attributable to its tab.
 * Falls back to the minimal sku/uid pair when no real section schema exists.
 * Never editable.
 */
export function buildAllSectionsColumnDefs(
  iqEnabled = false,
  schema: ColumnDef[] = [],
  opts?: { pricingColumns?: boolean; showHidden?: boolean },
) {
  const unifiedSchema: ColumnDef[] = schema.length
    ? schema
    : [
        { key: 'sku', label: 'Code (SKU)' },
        { key: 'uid', label: 'UID' },
      ]
  const sectionCol = {
    field: '__section',
    headerName: 'Section',
    editable: false,
    minWidth: 150,
    cellEditor: 'agTextCellEditor',
  }
  return [
    sectionCol,
    ...buildColumnDefs(unifiedSchema, {
      editable: false,
      iqEnabled,
      pricingColumns: opts?.pricingColumns,
      showHidden: opts?.showHidden,
    }),
  ]
}
